import { useState, useEffect } from "react";
import { ArrowRight, ChevronDown, Zap } from "lucide-react";
import { Button } from "@/components/v2/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/v2/ui/popover";

const LANGUAGES = [
  "Hindi", "Kannada", "Telugu", "Tamil", "Marathi",
  "Malayalam", "Gujarati", "Bengali", "Odia",
];

const CITIES = [
  "Bangalore", "Mumbai", "Pune", "Hyderabad", "Delhi", "Chennai",
];

const InstaBookWidget = () => {
  const [selectedLang, setSelectedLang] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [langOpen, setLangOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      const lang = e.detail;
      if (lang && LANGUAGES.includes(lang)) setSelectedLang(lang);
    };
    window.addEventListener("select-language", handler);
    return () => window.removeEventListener("select-language", handler);
  }, []);

  const isVirtual = selectedCity === "E-Pooja";
  const bookingUrl =
    selectedLang && selectedCity
      ? isVirtual
        ? `/${selectedLang.toLowerCase()}/e-pooja/pooja`
        : `/${selectedLang.toLowerCase()}/${selectedCity.toLowerCase()}/pooja`
      : null;

  return (
    <div className="bg-card rounded-2xl border border-primary/15 shadow-lg p-5 space-y-4">
      <div className="inline-flex items-center gap-1.5 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-semibold font-sans">
        <Zap className="h-3.5 w-3.5" />
        Insta Book
      </div>

      {/* Language */}
      <Popover open={langOpen} onOpenChange={setLangOpen}>
        <PopoverTrigger asChild>
          <button 
            aria-label={`Select language. Currently selected: ${selectedLang || "None"}`}
            className={`w-full text-left rounded-xl p-3 bg-background transition-all group shadow-sm hover:shadow-md border ${selectedLang ? "border-primary" : "border-border/60 hover:border-primary/60"} ${langOpen ? "ring-2 ring-primary/20" : ""}`}
          >
            <p className="text-xs font-medium text-muted-foreground mb-0.5">I prefer a priest who speaks</p>
            <div className="flex items-center justify-between">
              <span className={`text-sm font-semibold ${selectedLang ? "text-primary" : "text-foreground/50"}`}>
                {selectedLang || "Select language"}
              </span>
              <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${langOpen ? "rotate-180" : ""}`} />
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3" align="start">
          <div className="flex flex-wrap gap-1.5">
            {LANGUAGES.map((lang) => (
              <button
                key={lang}
                aria-label={`Choose ${lang}`}
                onClick={() => { setSelectedLang(lang); setLangOpen(false); }}
                className={`rounded-full px-4 py-2.5 text-sm font-semibold transition-all min-h-[40px] ${
                  selectedLang === lang
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "border border-border text-foreground hover:border-primary/50 hover:bg-primary/5"
                }`}
              >
                {lang}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {/* City */}
      <Popover open={cityOpen} onOpenChange={setCityOpen}>
        <PopoverTrigger asChild>
          <button 
            aria-label={`Select city. Currently selected: ${selectedCity || "None"}`}
            className={`w-full text-left rounded-xl p-3 bg-background transition-all group shadow-sm hover:shadow-md border ${selectedCity ? "border-primary" : "border-border/60 hover:border-primary/60"} ${cityOpen ? "ring-2 ring-primary/20" : ""}`}
          >
            <p className="text-xs font-medium text-muted-foreground mb-0.5">Pooja to be performed in</p>
            <div className="flex items-center justify-between">
              <span className={`text-sm font-semibold ${selectedCity ? "text-primary" : "text-foreground/50"}`}>
                {selectedCity === "E-Pooja" ? "🌐 E-Pooja (Virtual)" : selectedCity || "Select city"}
              </span>
              <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${cityOpen ? "rotate-180" : ""}`} />
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-3" align="start">
          <div className="flex flex-wrap gap-1.5">
            {CITIES.map((city) => (
              <button
                key={city}
                aria-label={`Choose ${city}`}
                onClick={() => { setSelectedCity(city); setCityOpen(false); }}
                className={`rounded-full px-4 py-2.5 text-sm font-semibold transition-all min-h-[40px] ${
                  selectedCity === city
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "border border-border text-foreground hover:border-primary/50 hover:bg-primary/5"
                }`}
              >
                {city}
              </button>
            ))}
            <button
              aria-label="Choose E-Pooja (Virtual)"
              onClick={() => { setSelectedCity("E-Pooja"); setCityOpen(false); }}
              className={`rounded-full px-4 py-2.5 text-sm font-semibold transition-all min-h-[40px] ${
                selectedCity === "E-Pooja"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "border border-border text-foreground hover:border-primary/50 hover:bg-primary/5"
              }`}
            >
              🌐 E-Pooja (Virtual)
            </button>
          </div>
        </PopoverContent>
      </Popover>

      {/* CTA */}
      <Button
        size="lg"
        className="w-full text-base font-semibold min-h-[48px] rounded-xl font-sans"
        disabled={!bookingUrl}
        asChild={!!bookingUrl}
      >
        {bookingUrl ? (
          <a href={bookingUrl} target="_blank" rel="noopener noreferrer">
            Browse Puja Packages <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        ) : (
          <span>Select language & city first</span>
        )}
      </Button>

      <p className="text-xs text-muted-foreground text-center font-sans">
        Priest details & prep list sent within 24 hrs · Partial payment accepted
      </p>
    </div>
  );
};

export default InstaBookWidget;
